import { ApiError, networkError, toApiError } from './errors';

/**
 * The transport.
 *
 * Deliberately platform-agnostic: no `window`, no `localStorage`, no
 * `AsyncStorage`, no Expo import. It takes `fetch` and a session adapter and
 * nothing else, which is what lets one client serve a React Native app and a
 * browser without a per-platform fork — and what lets its tests run in Node
 * with a fake `fetch` rather than a simulator.
 *
 * Three things happen here that must not be scattered across feature code:
 *
 * **Single-flight refresh.** Five requests failing 401 together must produce
 * one refresh, not five. Five would rotate the refresh token five times, and
 * with reuse detection on the server that revokes the session and signs the
 * person out — a refresh storm turning into a logout is a bug that only appears
 * under load.
 *
 * **Idempotency on unsafe retries.** A POST is never retried without a key. A
 * timeout means the request may or may not have been applied, and retrying an
 * unkeyed POST is how one leave request becomes two.
 *
 * **Request ids.** Generated here and echoed by the server, so an error a user
 * reports can be found in a log.
 */

export interface SessionAdapter {
  /** The current access token, or null when signed out. */
  getAccessToken(): string | null | Promise<string | null>;
  /**
   * Exchange the stored refresh credential for a new access token.
   *
   * Returns the new access token, or null when the session cannot be renewed —
   * expired, revoked, or reuse detected. Never throws for an expected failure.
   */
  refresh(): Promise<string | null>;
  /** Called once when a session is definitively over. */
  onUnauthorized(): void | Promise<void>;
}

export interface TransportConfig {
  baseUrl: string;
  session?: SessionAdapter;
  fetch?: typeof globalThis.fetch;
  /** Milliseconds before a request is abandoned. */
  timeoutMs?: number;
  /** Headers added to every request — app version, platform, locale. */
  defaultHeaders?: () => Record<string, string>;
  /** Somewhere to send request/response diagnostics. Never receives bodies. */
  onDiagnostic?: (entry: DiagnosticEntry) => void;
  /** Random id source. Injected so tests are deterministic. */
  generateId?: () => string;
}

export interface DiagnosticEntry {
  method: string;
  path: string;
  status: number;
  durationMs: number;
  requestId: string | null;
  errorCode?: string;
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  query?: Record<string, unknown>;
  body?: unknown;
  /**
   * Makes an unsafe request safe to repeat. Required before any retry of a
   * POST; supplying one is what turns "may have been applied" into "apply once".
   */
  idempotencyKey?: string;
  signal?: AbortSignal;
  timeoutMs?: number;
  /** Skip the Authorization header — sign-in and refresh only. */
  anonymous?: boolean;
  headers?: Record<string, string>;
}

export interface PageInfo {
  nextCursor: string | null;
  previousCursor: string | null;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  limit: number;
}

export interface Page<T> {
  items: T[];
  pageInfo: PageInfo;
  totalCount: number;
  filteredCount: number;
}

const DEFAULT_TIMEOUT_MS = 20_000;

function randomId(): string {
  // crypto.randomUUID exists in modern browsers, Node 19+ and Hermes with the
  // polyfill Expo ships. The fallback keeps the client usable where it does not.
  const crypto = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
  if (crypto?.randomUUID) return crypto.randomUUID();
  return `id-${Date.now().toString(36)}-${Math.floor(Math.random() * 1e9).toString(36)}`;
}

function buildQuery(query: Record<string, unknown> | undefined): string {
  if (!query) return '';

  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue;
    if (Array.isArray(value)) {
      for (const entry of value) params.append(key, String(entry));
    } else {
      params.set(key, String(value));
    }
  }

  const rendered = params.toString();
  return rendered ? `?${rendered}` : '';
}

export class ApiTransport {
  private readonly config: TransportConfig;
  private readonly fetchImpl: typeof globalThis.fetch;
  private readonly newId: () => string;

  /**
   * The in-flight refresh, shared by every request that discovers a 401 while
   * it is running. This single field is the whole of the single-flight rule.
   */
  private refreshInFlight: Promise<string | null> | null = null;

  constructor(config: TransportConfig) {
    this.config = config;
    this.fetchImpl = config.fetch ?? globalThis.fetch.bind(globalThis);
    this.newId = config.generateId ?? randomId;
  }

  async request<T>(path: string, options: RequestOptions = {}): Promise<T> {
    const attempt = await this.send<T>(path, options, false);
    return attempt;
  }

  get<T>(path: string, options: Omit<RequestOptions, 'method' | 'body'> = {}): Promise<T> {
    return this.request<T>(path, { ...options, method: 'GET' });
  }

  post<T>(path: string, body?: unknown, options: RequestOptions = {}): Promise<T> {
    return this.request<T>(path, { ...options, method: 'POST', body });
  }

  patch<T>(path: string, body?: unknown, options: RequestOptions = {}): Promise<T> {
    return this.request<T>(path, { ...options, method: 'PATCH', body });
  }

  put<T>(path: string, body?: unknown, options: RequestOptions = {}): Promise<T> {
    return this.request<T>(path, { ...options, method: 'PUT', body });
  }

  delete<T>(path: string, options: RequestOptions = {}): Promise<T> {
    return this.request<T>(path, { ...options, method: 'DELETE' });
  }

  /**
   * A list endpoint, with the envelope's paging fields lifted out.
   *
   * The whole envelope is kept rather than unwrapped to `data`, which `send`
   * decides from `rawEnvelope` — an internal flag, not a header.
   *
   * It was a header once: `x-envelope: page`. That worked in Node and failed in
   * every browser, because a header the server's CORS policy does not list
   * blocks the entire request — and the failure arrives as a network error, so
   * every paginated screen reported the server unreachable while the server was
   * answering. A header sent purely to communicate with a function twenty lines
   * below has no business crossing the network.
   */
  async page<T>(
    path: string,
    options: Omit<RequestOptions, 'method' | 'body'> = {},
  ): Promise<Page<T>> {
    const body = await this.send<{
      data: T[];
      pageInfo?: PageInfo;
      totalCount?: number;
      filteredCount?: number;
    }>(path, { ...options, method: 'GET' }, false, true);

    /*
     * Not every list endpoint pages. Some answer the plain `{data, meta}`
     * envelope, and reading paging fields off it left `totalCount` undefined on
     * every screen that showed a total. A plain list is one whole page: the
     * counts are the items', and there is nowhere further to go. Endpoints that
     * do send the paginated envelope pass through untouched.
     */
    const items = body.data ?? [];

    return {
      items,
      pageInfo: body.pageInfo ?? {
        nextCursor: null,
        previousCursor: null,
        hasNextPage: false,
        hasPreviousPage: false,
        limit: items.length,
      },
      totalCount: body.totalCount ?? items.length,
      filteredCount: body.filteredCount ?? items.length,
    };
  }

  private async send<T>(
    path: string,
    options: RequestOptions,
    isRetry: boolean,
    rawEnvelope = false,
  ): Promise<T> {
    const method = options.method ?? 'GET';
    const url = `${this.config.baseUrl}${path}${buildQuery(options.query)}`;
    const requestId = this.newId();
    const startedAt = Date.now();

    const headers: Record<string, string> = {
      accept: 'application/json',
      'x-request-id': requestId,
      ...(this.config.defaultHeaders?.() ?? {}),
      ...options.headers,
    };

    if (options.body !== undefined) headers['content-type'] = 'application/json';
    if (options.idempotencyKey) headers['idempotency-key'] = options.idempotencyKey;

    if (!options.anonymous && this.config.session) {
      const token = await this.config.session.getAccessToken();
      if (token) headers.authorization = `Bearer ${token}`;
    }

    // The caller's own abort signal and our timeout both have to be able to
    // cancel; whichever fires first wins.
    const controller = new AbortController();
    const timeout = setTimeout(
      () => controller.abort(),
      options.timeoutMs ?? this.config.timeoutMs ?? DEFAULT_TIMEOUT_MS,
    );
    const onExternalAbort = () => controller.abort();
    options.signal?.addEventListener('abort', onExternalAbort);

    let response: Response;
    try {
      response = await this.fetchImpl(url, {
        method,
        headers,
        body: options.body === undefined ? undefined : JSON.stringify(options.body),
        signal: controller.signal,
        // The cookie is how the web client's refresh token travels; it is
        // absent and harmless on React Native.
        credentials: 'include',
      });
    } catch (cause) {
      clearTimeout(timeout);
      options.signal?.removeEventListener('abort', onExternalAbort);
      const error = networkError(cause);
      this.config.onDiagnostic?.({
        method,
        path,
        status: 0,
        durationMs: Date.now() - startedAt,
        requestId,
        errorCode: error.code,
      });
      throw error;
    } finally {
      clearTimeout(timeout);
      options.signal?.removeEventListener('abort', onExternalAbort);
    }

    const text = await response.text();
    const parsed: unknown = text ? safeJson(text) : null;

    this.config.onDiagnostic?.({
      method,
      path,
      status: response.status,
      durationMs: Date.now() - startedAt,
      requestId: response.headers.get('x-request-id') ?? requestId,
    });

    if (response.ok) {
      // A page response is returned whole so `page()` can read the paging
      // fields; everything else is unwrapped to its `data`.
      if (rawEnvelope) return parsed as T;
      const envelope = parsed as { data?: T } | null;
      return (envelope?.data ?? (parsed as T)) as T;
    }

    if (response.status === 401 && !options.anonymous && !isRetry && this.config.session) {
      const renewed = await this.refreshOnce();

      if (renewed) {
        /*
         * Retried once, and only once.
         *
         * An unsafe method is retried only when the caller gave an idempotency
         * key: without one, a request that the server may already have applied
         * would be applied a second time. A GET is always safe to repeat.
         */
        const safe = method === 'GET' || Boolean(options.idempotencyKey);
        if (safe) return this.send<T>(path, options, true, rawEnvelope);
      }

      await this.config.session.onUnauthorized();
    }

    throw toApiError({
      status: response.status,
      body: parsed,
      requestId: response.headers.get('x-request-id') ?? requestId,
    });
  }

  /**
   * Refresh, at most once at a time.
   *
   * Everybody who arrives while a refresh is running awaits the same promise.
   * Without this, five concurrent 401s rotate the refresh token five times and
   * the server's reuse detection — correctly — revokes the session.
   */
  private refreshOnce(): Promise<string | null> {
    if (!this.config.session) return Promise.resolve(null);

    this.refreshInFlight ??= this.config.session
      .refresh()
      .catch(() => null)
      .finally(() => {
        this.refreshInFlight = null;
      });

    return this.refreshInFlight;
  }
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

export { ApiError };
